import { supabase } from '../lib/supabase'
import { getMcpChatUrl } from './mcpConfig'

export type McpChatMessage = {
  role: 'user' | 'assistant' | 'system'
  content: string
}

export type McpChatToolEvent = {
  name: string
  arguments?: string
  message: string
}

export type McpChatDoneEvent = {
  reply: string
  toolCalls?: unknown[]
}

export type StreamChatHandlers = {
  onTool?: (event: McpChatToolEvent) => void
  onToken?: (content: string) => void
  onDone?: (event: McpChatDoneEvent) => void
  onError?: (message: string) => void
}

async function getAccessToken(): Promise<string> {
  const { data, error } = await supabase.auth.getSession()
  if (error) throw error
  const token = data.session?.access_token
  if (!token) throw new Error('Sessão expirada. Faça login novamente.')
  return token
}

function parseSseBlock(
  block: string,
  handlers: StreamChatHandlers,
): void {
  const lines = block.split('\n')
  const event = lines.find((l) => l.startsWith('event: '))?.slice(7)?.trim()
  const dataLine = lines.find((l) => l.startsWith('data: '))?.slice(6)
  if (!event || dataLine == null) return

  let data: unknown
  try {
    data = JSON.parse(dataLine)
  } catch {
    return
  }

  if (event === 'tool') {
    const payload = data as McpChatToolEvent
    handlers.onTool?.(payload)
  } else if (event === 'token') {
    const payload = data as { content?: string }
    if (payload.content) handlers.onToken?.(payload.content)
  } else if (event === 'done') {
    handlers.onDone?.(data as McpChatDoneEvent)
  } else if (event === 'error') {
    const payload = data as { message?: string }
    handlers.onError?.(payload.message || 'Erro no chat MCP')
  }
}

/**
 * Chama POST /chat do MCP com JWT do usuário PasiAI (SSE).
 * Não envia MCP_AUTH_TOKEN — o token de máquina fica só no servidor MCP / Cursor.
 */
export async function streamMcpChat(
  messages: McpChatMessage[],
  handlers: StreamChatHandlers,
  signal?: AbortSignal,
): Promise<void> {
  const chatUrl = getMcpChatUrl()
  const accessToken = await getAccessToken()

  let res: Response
  try {
    res = await fetch(chatUrl, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
      },
      body: JSON.stringify({ messages, stream: true }),
      signal,
    })
  } catch (err) {
    if ((err as { name?: string })?.name === 'AbortError') throw err
    const message =
      `Não foi possível conectar ao chat MCP em ${chatUrl}. ` +
      'Verifique se VITE_MCP_URL está correta e se CORS/ALLOWED_ORIGINS permite esta origem.'
    handlers.onError?.(message)
    throw new Error(message)
  }

  if (!res.ok) {
    let message = `Chat HTTP ${res.status}`
    try {
      const body = (await res.json()) as { error?: string }
      if (body.error) message = body.error
    } catch {
      /* ignore */
    }
    handlers.onError?.(message)
    throw new Error(message)
  }

  if (!res.body) {
    const message = 'Resposta SSE sem corpo'
    handlers.onError?.(message)
    throw new Error(message)
  }

  const reader = res.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })

    const parts = buffer.split('\n\n')
    buffer = parts.pop() ?? ''

    for (const part of parts) {
      if (part.trim()) parseSseBlock(part, handlers)
    }
  }

  if (buffer.trim()) {
    parseSseBlock(buffer, handlers)
  }
}

export function buildBrokerStrategicAnalysisPrompt(params: {
  brokerId: string
  idScgp: number | null
  brokerName: string
}): string {
  const scgp = params.idScgp != null ? String(params.idScgp) : 'desconhecido'
  return [
    `Faça uma ANÁLISE ESTRATÉGICA E HISTÓRICA do corretor (${params.brokerName}).`,
    `UUID: ${params.brokerId}. SCGP: ${scgp}.`,
    'Use as tools get_broker_profile, get_broker_campaigns, get_broker_quotes, get_broker_activities e get_broker_rewards como insumo.',
    'Não repita inventário cadastral nem espelhe as abas do Cockpit (identificação, campanhas, cotações, atividades, pontos/cupons já estão na UI).',
    'Estruture a resposta nestas seções: 1) Síntese executiva 2) Trajetória e momento 3) Oportunidades 4) Riscos e atritos 5) Plano de ação sugerido.',
    'Seja estratégico, cite poucos indicadores só para sustentar a tese e não invente dados.',
  ].join(' ')
}

/** @deprecated Use buildBrokerStrategicAnalysisPrompt */
export function buildBrokerStandardSummaryPrompt(params: {
  brokerId: string
  idScgp: number | null
  brokerName: string
}): string {
  return buildBrokerStrategicAnalysisPrompt(params)
}
